import { useCallback, useMemo, useState } from "react";
import type { EstimatedCoverage } from "api/types";
import {
  buildPeriodDeviationKey,
  buildPeriodKey,
  buildPeriodStartIso,
  collectSimulationDeviationNotePayloads,
  createEmptyDeviationNotesState,
  type SimulationDeviationNoteCreateInput,
  type SimulationDeviationNotesState,
  type SimulationPeriodDeviationDraft,
} from "../types/simulationDeviationNotes";

export function useSimulationDeviationNotes() {
  const [state, setState] = useState<SimulationDeviationNotesState>(
    createEmptyDeviationNotesState,
  );

  const setProductGeneralReason = useCallback(
    (productId: number, deviationNoteId: string, label: string) => {
      setState((prev) => ({
        ...prev,
        byProductGeneral: {
          ...prev.byProductGeneral,
          [productId]: { deviationNoteId, label },
        },
      }));
    },
    [],
  );

  const getPeriodDeviationNoteId = useCallback(
    (productId: number, periodIndex: number): string | null => {
      const key = buildPeriodDeviationKey(productId, periodIndex);
      return state.byProductPeriod[key]?.deviationNoteId ?? null;
    },
    [state.byProductPeriod],
  );

  const getProductGeneralNoteId = useCallback(
    (productId: number): string | null =>
      state.byProductGeneral[productId]?.deviationNoteId ?? null,
    [state.byProductGeneral],
  );

  const getProductGeneralNoteLabel = useCallback(
    (productId: number): string | null =>
      state.byProductGeneral[productId]?.label ?? null,
    [state.byProductGeneral],
  );

  const getPeriodDeviationNoteLabel = useCallback(
    (productId: number, periodIndex: number): string | null => {
      const key = buildPeriodDeviationKey(productId, periodIndex);
      return state.byProductPeriod[key]?.deviationNoteLabel ?? null;
    },
    [state.byProductPeriod],
  );

  const setPeriodDeviation = useCallback(
    (input: {
      productId: number;
      erpCode: string;
      periodIndex: number;
      targetDate: EstimatedCoverage["targetDate"];
      chronologyParameter: string;
      deviationNoteId: string | null;
      deviationNoteLabel?: string;
      simulationResultProductId?: string;
      comments?: string;
    }) => {
      const periodKey = buildPeriodKey(input.targetDate, input.chronologyParameter);
      const compositeKey = buildPeriodDeviationKey(
        input.productId,
        input.periodIndex,
      );
      const periodStartDate = buildPeriodStartIso(
        input.targetDate,
        input.chronologyParameter,
      );

      const draft: SimulationPeriodDeviationDraft = {
        productId: input.productId,
        erpCode: input.erpCode,
        periodKey,
        periodStartDate,
        periodIndex: input.periodIndex,
        deviationNoteId: input.deviationNoteId,
        deviationNoteLabel: input.deviationNoteLabel,
        comments: input.comments,
        simulationResultProductId: input.simulationResultProductId,
      };

      setState((prev) => {
        if (input.deviationNoteId == null || input.deviationNoteId === "") {
          if (!prev.byProductPeriod[compositeKey]) {
            return prev;
          }
          const next = { ...prev.byProductPeriod };
          delete next[compositeKey];
          return { ...prev, byProductPeriod: next };
        }
        return {
          ...prev,
          byProductPeriod: {
            ...prev.byProductPeriod,
            [compositeKey]: draft,
          },
        };
      });
    },
    [],
  );

  const resetPeriodDeviations = useCallback(() => {
    setState((prev) => ({
      ...prev,
      byProductPeriod: {},
    }));
  }, []);

  /** Reemplaza el estado (no fusiona) para no arrastrar desvíos de otra simulación. */
  const hydrateDeviationNotesState = useCallback(
    (partial: Partial<SimulationDeviationNotesState>) => {
      setState({
        byProductGeneral: partial.byProductGeneral ?? {},
        byProductPeriod: partial.byProductPeriod ?? {},
      });
    },
    [],
  );

  const resetDeviationNotesState = useCallback(() => {
    setState(createEmptyDeviationNotesState());
  }, []);

  const getPayloadForSimulation = useCallback(
    (
      simulationId: string,
      erpCodeByProductId: Map<number, string>,
    ): SimulationDeviationNoteCreateInput[] =>
      collectSimulationDeviationNotePayloads(
        state,
        simulationId,
        erpCodeByProductId,
      ),
    [state],
  );

  const periodDeviationCount = useMemo(
    () => Object.keys(state.byProductPeriod).length,
    [state.byProductPeriod],
  );

  return {
    deviationNotesState: state,
    setProductGeneralReason,
    getPeriodDeviationNoteId,
    getProductGeneralNoteId,
    getProductGeneralNoteLabel,
    getPeriodDeviationNoteLabel,
    setPeriodDeviation,
    resetPeriodDeviations,
    hydrateDeviationNotesState,
    resetDeviationNotesState,
    getPayloadForSimulation,
    periodDeviationCount,
  };
}

export type UseSimulationDeviationNotesReturn = ReturnType<
  typeof useSimulationDeviationNotes
>;
